import { useState } from "react";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { MxTabBar } from "@/components/shared/MxTabBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ClipboardList, Plus, Pencil, Trash2, FileDown, Wrench, MapPin, Check, X, Scroll,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  usePmProgramsAdmin,
  useCreatePmProgram,
  useUpdatePmProgram,
  useDeletePmProgram,
  usePmProgramAssets,
  type PmProgram,
  type PmProgramAsset,
} from "@/hooks/usePmPrograms";
import { useAssetServiceLog, useAddServiceLogEntry, useDeleteServiceLogEntry } from "@/hooks/useAssetServiceLog";
import { useOrganization } from "@/hooks/useOrganization";
import { useUserProfile } from "@/hooks/useUserProfile";
import { printPmWorksheet } from "@/lib/printPmWorksheet";
import { cn } from "@/lib/utils";
import { format } from "date-fns";

// ── Asset table grouped by facility/zone ─────────────────────────────────────

type SortMode = "location" | "workflow";

function groupAssets(assets: PmProgramAsset[]) {
  const map = new Map<string, { facilityName: string; zones: Map<string, { zoneName: string; assets: PmProgramAsset[] }> }>();
  for (const a of assets) {
    const fKey = a.facility_id ?? "__none__";
    const fName = a.facility_name ?? "No Facility";
    const zKey = a.zone_id ?? "__none__";
    const zName = a.zone_name ?? "No Zone";
    if (!map.has(fKey)) map.set(fKey, { facilityName: fName, zones: new Map() });
    const f = map.get(fKey)!;
    if (!f.zones.has(zKey)) f.zones.set(zKey, { zoneName: zName, assets: [] });
    f.zones.get(zKey)!.assets.push(a);
  }
  // Sort within zone
  for (const f of map.values())
    for (const z of f.zones.values())
      z.assets.sort((a, b) => (a.asset_type ?? "").localeCompare(b.asset_type ?? "") || a.name.localeCompare(b.name));
  return map;
}

/** Groups by facility → asset type — useful when technicians service all units
 *  of the same equipment type in a building before moving to the next type. */
function groupAssetsByWorkflow(assets: PmProgramAsset[]) {
  const map = new Map<string, { facilityName: string; types: Map<string, { typeName: string; assets: PmProgramAsset[] }> }>();
  for (const a of assets) {
    const fKey = a.facility_id ?? "__none__";
    const fName = a.facility_name ?? "No Facility";
    const tKey = a.asset_type ?? "__none__";
    const tName = a.asset_type ? a.asset_type.replace(/_/g, " ") : "Unclassified";
    if (!map.has(fKey)) map.set(fKey, { facilityName: fName, types: new Map() });
    const f = map.get(fKey)!;
    if (!f.types.has(tKey)) f.types.set(tKey, { typeName: tName, assets: [] });
    f.types.get(tKey)!.assets.push(a);
  }
  // Sort within type by name
  for (const f of map.values())
    for (const t of f.types.values())
      t.assets.sort((a, b) => a.name.localeCompare(b.name));
  return map;
}

const STATUS_COLORS: Record<string, string> = {
  online: "bg-green-100 text-green-800",
  offline: "bg-gray-100 text-gray-800",
  maintenance: "bg-yellow-100 text-yellow-800",
  decommissioned: "bg-red-100 text-red-800",
};

// ── Service History Dialog ────────────────────────────────────────────────

function ServiceHistoryDialog({
  asset,
  open,
  onOpenChange,
}: {
  asset: PmProgramAsset | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { data: entries = [] } = useAssetServiceLog(asset?.id ?? null);
  const addEntry = useAddServiceLogEntry();
  const deleteEntry = useDeleteServiceLogEntry();

  const [adding, setAdding] = useState(false);
  const [note, setNote] = useState("");
  const [performedBy, setPerformedBy] = useState("");
  const [performedAt, setPerformedAt] = useState(
    () => new Date().toISOString().slice(0, 10)
  );

  const handleAdd = () => {
    if (!asset || !note.trim()) return;
    addEntry.mutate(
      { assetId: asset.id, note: note.trim(), performedBy: performedBy.trim() || "—", performedAt },
      {
        onSuccess: () => {
          setNote("");
          setPerformedBy("");
          setPerformedAt(new Date().toISOString().slice(0, 10));
          setAdding(false);
        },
      }
    );
  };

  if (!asset) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scroll className="h-4 w-4 text-muted-foreground" />
            Service History — {asset.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 mt-1">
          {entries.length === 0 && !adding && (
            <p className="text-sm text-muted-foreground py-4 text-center">No service entries yet.</p>
          )}

          {entries.length > 0 && (
            <div className="rounded-lg border overflow-hidden text-sm">
              {entries.map((e) => (
                <div key={e.id} className="flex items-start gap-2 px-3 py-2.5 border-b last:border-b-0 group">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm">{e.note}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {e.performed_by && e.performed_by !== "—" && `${e.performed_by} · `}
                      {format(new Date(e.performed_at), "MMM d, yyyy")}
                    </p>
                  </div>
                  <button
                    onClick={() => deleteEntry.mutate({ id: e.id, assetId: asset.id })}
                    className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity shrink-0 mt-0.5"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {!adding ? (
            <button
              onClick={() => setAdding(true)}
              className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
            >
              <Plus className="h-3 w-3" />
              Add entry
            </button>
          ) : (
            <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
              <Textarea
                autoFocus
                placeholder="What was done?"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                className="text-sm resize-none"
              />
              <div className="flex items-center gap-2">
                <Input
                  placeholder="Performed by"
                  value={performedBy}
                  onChange={(e) => setPerformedBy(e.target.value)}
                  className="h-8 text-sm flex-1"
                />
                <Input
                  type="date"
                  value={performedAt}
                  onChange={(e) => setPerformedAt(e.target.value)}
                  className="h-8 text-sm w-36"
                />
              </div>
              <div className="flex items-center gap-2 pt-1">
                <Button size="sm" onClick={handleAdd} disabled={!note.trim() || addEntry.isPending}>
                  {addEntry.isPending ? "Saving…" : "Save"}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setAdding(false); setNote(""); setPerformedBy(""); }}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AssetTable({
  rows,
  showZone,
  onViewHistory,
}: {
  rows: PmProgramAsset[];
  showZone: boolean;
  onViewHistory: (asset: PmProgramAsset) => void;
}) {
  return (
    <div className="rounded-lg border overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-muted/50 border-b">
            <th className="text-left px-3 py-2 text-xs font-semibold text-muted-foreground">Asset</th>
            {showZone && <th className="text-left px-3 py-2 text-xs font-semibold text-muted-foreground">Zone</th>}
            {!showZone && <th className="text-left px-3 py-2 text-xs font-semibold text-muted-foreground">Type</th>}
            <th className="text-left px-3 py-2 text-xs font-semibold text-muted-foreground">Serial #</th>
            <th className="text-left px-3 py-2 text-xs font-semibold text-muted-foreground">Make / Model</th>
            <th className="px-3 py-2 text-xs font-semibold text-muted-foreground">Status</th>
            <th className="px-3 py-2 w-8"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((a) => (
            <tr key={a.id} className="border-b last:border-b-0 hover:bg-muted/30 transition-colors group">
              <td className="px-3 py-2.5 font-medium">{a.name}</td>
              {showZone && (
                <td className="px-3 py-2.5 text-muted-foreground text-xs">{a.zone_name ?? "—"}</td>
              )}
              {!showZone && (
                <td className="px-3 py-2.5 capitalize text-muted-foreground text-xs">
                  {a.asset_type?.replace(/_/g, " ") ?? "—"}
                </td>
              )}
              <td className="px-3 py-2.5 font-mono text-xs text-muted-foreground">
                {a.serial_number ?? "—"}
              </td>
              <td className="px-3 py-2.5 text-muted-foreground text-xs">
                {[a.make, a.model].filter(Boolean).join(" ") || "—"}
              </td>
              <td className="px-3 py-2.5">
                <Badge className={cn("text-xs", STATUS_COLORS[a.status] ?? "")}>{a.status}</Badge>
              </td>
              <td className="px-2 py-2.5 text-right">
                <button
                  onClick={() => onViewHistory(a)}
                  title="Service history"
                  className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground"
                >
                  <Scroll className="h-3.5 w-3.5" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ProgramAssetList({ program, orgName, orgLogoUrl }: { program: PmProgram; orgName: string | null; orgLogoUrl: string | null }) {
  const { data: assets = [], isLoading } = usePmProgramAssets(program.id);
  const [sortMode, setSortMode] = useState<SortMode>("location");
  const [historyAsset, setHistoryAsset] = useState<PmProgramAsset | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  const handleViewHistory = (asset: PmProgramAsset) => {
    setHistoryAsset(asset);
    setHistoryOpen(true);
  };

  const groupedByLocation = groupAssets(assets);
  const groupedByWorkflow = groupAssetsByWorkflow(assets);

  const handlePrint = () => {
    printPmWorksheet(program, assets, { name: orgName, logo_url: orgLogoUrl }, sortMode);
  };

  return (
    <div className="flex-1">
      {/* Program header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold">{program.name}</h2>
          {program.description && (
            <p className="text-sm text-muted-foreground mt-0.5">{program.description}</p>
          )}
          <p className="text-xs text-muted-foreground mt-1">{assets.length} asset{assets.length !== 1 ? "s" : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Sort mode toggle */}
          <div className="flex items-center rounded-lg border p-0.5 gap-0.5">
            <Button
              variant={sortMode === "location" ? "secondary" : "ghost"}
              size="sm"
              className="h-7 px-2.5 text-xs gap-1.5"
              onClick={() => setSortMode("location")}
            >
              <MapPin className="h-3 w-3" />
              By Location
            </Button>
            <Button
              variant={sortMode === "workflow" ? "secondary" : "ghost"}
              size="sm"
              className="h-7 px-2.5 text-xs gap-1.5"
              onClick={() => setSortMode("workflow")}
            >
              <Wrench className="h-3 w-3" />
              By Workflow
            </Button>
          </div>
          <Button variant="outline" size="sm" onClick={handlePrint} className="gap-1.5">
            <FileDown className="h-4 w-4" />
            Export PDF
          </Button>
        </div>
      </div>

      <Separator className="mb-4" />

      {isLoading && <p className="text-sm text-muted-foreground">Loading assets…</p>}

      {!isLoading && assets.length === 0 && (
        <div className="flex flex-col items-center text-center py-24 text-muted-foreground">
          <ClipboardList className="h-10 w-10 mb-3 opacity-20" />
          <p className="text-sm font-medium">No assets assigned</p>
          <p className="text-xs mt-1">Open any asset and check its PM programs in Edit mode.</p>
        </div>
      )}

      <div className="overflow-y-auto flex-1 space-y-6">
        {sortMode === "location" && Array.from(groupedByLocation.values()).map(({ facilityName, zones }) => (
          <div key={facilityName}>
            <div className="flex items-center gap-2 mb-3">
              <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-sm font-semibold">{facilityName}</span>
            </div>
            {Array.from(zones.values()).map(({ zoneName, assets: zAssets }) => (
              <div key={zoneName} className="mb-4 ml-5">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">{zoneName}</p>
                <AssetTable rows={zAssets} showZone={false} onViewHistory={handleViewHistory} />
              </div>
            ))}
          </div>
        ))}

        {sortMode === "workflow" && Array.from(groupedByWorkflow.values()).map(({ facilityName, types }) => (
          <div key={facilityName}>
            <div className="flex items-center gap-2 mb-3">
              <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-sm font-semibold">{facilityName}</span>
            </div>
            {Array.from(types.values()).map(({ typeName, assets: tAssets }) => (
              <div key={typeName} className="mb-4 ml-5">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 capitalize">{typeName}</p>
                <AssetTable rows={tAssets} showZone={true} onViewHistory={handleViewHistory} />
              </div>
            ))}
          </div>
        ))}
      </div>

      <ServiceHistoryDialog
        asset={historyAsset}
        open={historyOpen}
        onOpenChange={setHistoryOpen}
      />
    </div>
  );
}

// ── Program list sidebar ──────────────────────────────────────────────────────

interface EditingProgram {
  id: string | null; // null = new
  name: string;
  description: string;
}

function ProgramSidebar({
  programs,
  selectedId,
  onSelect,
  isAdmin,
}: {
  programs: PmProgram[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  isAdmin: boolean;
}) {
  const createProgram = useCreatePmProgram();
  const updateProgram = useUpdatePmProgram();
  const deleteProgram = useDeletePmProgram();

  const [editing, setEditing] = useState<EditingProgram | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PmProgram | null>(null);

  const startNew = () => setEditing({ id: null, name: "", description: "" });
  const startEdit = (p: PmProgram) => setEditing({ id: p.id, name: p.name, description: p.description ?? "" });
  const cancelEdit = () => setEditing(null);

  const handleSave = () => {
    if (!editing || !editing.name.trim()) return;
    if (editing.id) {
      updateProgram.mutate(
        { id: editing.id, name: editing.name.trim(), description: editing.description.trim() || null },
        { onSuccess: () => setEditing(null) }
      );
    } else {
      createProgram.mutate(
        { name: editing.name.trim(), description: editing.description.trim() || undefined, sort_order: programs.length + 1 },
        { onSuccess: () => setEditing(null) }
      );
    }
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteProgram.mutate(deleteTarget.id, {
      onSuccess: () => setDeleteTarget(null),
    });
  };

  return (
    <div className="w-64 shrink-0 border-r pr-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-semibold">Programs</p>
        {isAdmin && (
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={startNew}>
            <Plus className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto space-y-1 pt-0.5">
        {programs.map((p) => {
          const isSelected = p.id === selectedId;
          const isEditingThis = editing?.id === p.id;

          if (isEditingThis) {
            return (
              <div key={p.id} className="rounded-lg border bg-muted/40 p-2 space-y-2">
                <Input
                  autoFocus
                  value={editing.name}
                  onChange={(e) => setEditing((prev) => prev && { ...prev, name: e.target.value })}
                  placeholder="Program name"
                  className="h-7 text-sm"
                />
                <Textarea
                  value={editing.description}
                  onChange={(e) => setEditing((prev) => prev && { ...prev, description: e.target.value })}
                  placeholder="Description (optional)"
                  rows={2}
                  className="text-sm resize-none"
                />
                <div className="flex gap-1 justify-end">
                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={cancelEdit}>
                    <X className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="icon" className="h-6 w-6" onClick={handleSave} disabled={updateProgram.isPending}>
                    <Check className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            );
          }

          return (
            <div
              key={p.id}
              onClick={() => onSelect(p.id)}
              className={cn(
                "flex items-center gap-1 rounded-lg px-3 py-2.5 cursor-pointer group transition-colors",
                isSelected ? "bg-primary/10 text-primary" : "hover:bg-muted/60"
              )}
            >
              <ClipboardList className={cn("h-3.5 w-3.5 shrink-0", isSelected ? "text-primary" : "text-muted-foreground")} />
              <span className="flex-1 text-sm truncate">{p.name}</span>
              {isAdmin && (
                <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={(e) => { e.stopPropagation(); startEdit(p); }}
                    className="p-0.5 hover:text-foreground text-muted-foreground"
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setDeleteTarget(p); }}
                    className="p-0.5 hover:text-destructive text-muted-foreground"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>
          );
        })}

        {/* New program inline form */}
        {editing?.id === null && (
          <div className="rounded-lg border bg-muted/40 p-2 space-y-2">
            <Input
              autoFocus
              value={editing.name}
              onChange={(e) => setEditing((prev) => prev && { ...prev, name: e.target.value })}
              placeholder="Program name"
              className="h-7 text-sm"
              onKeyDown={(e) => { if (e.key === "Enter") handleSave(); if (e.key === "Escape") cancelEdit(); }}
            />
            <Textarea
              value={editing.description}
              onChange={(e) => setEditing((prev) => prev && { ...prev, description: e.target.value })}
              placeholder="Description (optional)"
              rows={2}
              className="text-sm resize-none"
            />
            <div className="flex gap-1 justify-end">
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={cancelEdit}>
                <X className="h-3.5 w-3.5" />
              </Button>
              <Button size="icon" className="h-6 w-6" onClick={handleSave} disabled={createProgram.isPending || !editing.name.trim()}>
                <Check className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the program. Assets will not be deleted, but their assignment to this program will be removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function PmPrograms() {
  const { data: programs = [], isLoading } = usePmProgramsAdmin();
  const { data: organization } = useOrganization();
  const { data: profile } = useUserProfile();
  const isAdmin = (profile as any)?.user_role?.some((ur: any) => ur.role?.name === "admin");

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selectedProgram = programs.find((p) => p.id === selectedId) ?? null;

  // Auto-select first program
  if (!selectedId && programs.length > 0) {
    setSelectedId(programs[0].id);
  }

  const sortedPrograms = [...programs].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="flex flex-col min-h-screen">
      <header className="sticky top-0 z-20 flex h-16 items-center gap-4 border-b bg-card px-6">
        <SidebarTrigger />
        <h1 className="text-lg font-semibold">Prev. Maint. Programs</h1>
      </header>
      <MxTabBar />

      <div className="flex gap-6 p-6">
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading programs…</p>
        ) : programs.length === 0 ? (
          <div className="flex flex-col items-center justify-center flex-1 text-center text-muted-foreground">
            <ClipboardList className="h-12 w-12 mb-3 opacity-20" />
            <p className="font-medium">No PM programs yet</p>
            {isAdmin && <p className="text-sm mt-1">Click + to create your first program.</p>}
          </div>
        ) : (
          <>
            <ProgramSidebar
              programs={sortedPrograms}
              selectedId={selectedId}
              onSelect={setSelectedId}
              isAdmin={isAdmin}
            />
            <div className="flex-1">
              {selectedProgram ? (
                <ProgramAssetList
                  program={selectedProgram}
                  orgName={organization?.name ?? null}
                  orgLogoUrl={organization?.logo_url ?? null}
                />
              ) : (
                <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
                  Select a program to view its assets.
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
